import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import SocialIcons from "./SocialIcons";
import { scrollToId } from "./SmoothScroll";
import { OFFICE, CATEGORIES, openTriage } from "../data/office";

const linkCls = "text-sm font-medium tracking-wide text-mist transition-colors hover:text-gold-400";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fecha os menus ao trocar de página
  useEffect(() => {
    setAreasOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  // Trava a rolagem da página com o menu do celular aberto
  useEffect(() => {
    if (mobileOpen) window.__lenis?.stop();
    else window.__lenis?.start();
    document.body.style.overflow = mobileOpen ? "hidden" : "";
  }, [mobileOpen]);

  const go = (id) => {
    setMobileOpen(false);
    setAreasOpen(false);
    scrollToId(id);
  };

  const openAreas = () => {
    clearTimeout(closeTimer.current);
    setAreasOpen(true);
  };
  const closeAreasSoon = () => {
    closeTimer.current = setTimeout(() => setAreasOpen(false), 150);
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled || mobileOpen
          ? "bg-navy-950/95 backdrop-blur-xl border-b border-gold-500/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          data-testid="nav-logo"
          onClick={() => {
            if (pathname === "/") window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo(0, 0);
          }}
          aria-label="Início"
        >
          <Logo />
        </Link>

        <nav className="flex items-center gap-6">
          {/* Menu suspenso de áreas — computador */}
          <div className="relative hidden lg:block" onMouseEnter={openAreas} onMouseLeave={closeAreasSoon}>
            <button
              data-testid="nav-areas-link"
              onClick={() => setAreasOpen((v) => !v)}
              aria-expanded={areasOpen}
              aria-haspopup="true"
              className={`flex items-center gap-1.5 ${linkCls} ${areasOpen ? "text-gold-400" : ""}`}
            >
              Áreas de atuação
              <ChevronDown className={`h-4 w-4 transition-transform ${areasOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {areasOpen && (
                <motion.div
                  data-testid="nav-areas-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 top-full w-80 -translate-x-1/2 pt-4"
                >
                  <div className="overflow-hidden rounded-2xl border border-gold-500/25 bg-navy-900 shadow-2xl">
                    <div className="h-px w-full gold-rule" />
                    <ul className="p-2">
                      {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        return (
                          <li key={c.slug}>
                            <Link
                              to={`/areas/${c.slug}`}
                              data-testid={`nav-area-${c.slug}`}
                              className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-cream transition-colors hover:bg-navy-800 hover:text-gold-400"
                            >
                              <Icon className="h-4 w-4 text-gold-400" />
                              {c.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                    <button
                      onClick={() => go("#areas")}
                      className="flex w-full items-center justify-between border-t border-navy-700 px-6 py-3.5 text-xs font-semibold uppercase tracking-widest text-mist transition-colors hover:text-gold-400"
                    >
                      Ver todos os serviços <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button data-testid="nav-locations-link" onClick={() => go("#localizacoes")} className={`hidden lg:block ${linkCls}`}>
            Localizações
          </button>
          <button data-testid="nav-faq-link" onClick={() => go("#duvidas")} className={`hidden xl:block ${linkCls}`}>
            Dúvidas
          </button>
          <button data-testid="nav-contact-link" onClick={() => go("#contato")} className={`hidden lg:block ${linkCls}`}>
            Contato
          </button>
          <button
            data-testid="nav-whatsapp-button"
            onClick={() => openTriage()}
            className="hidden rounded-full border border-gold-500/50 px-5 py-2 text-sm font-semibold text-gold-400 transition-all duration-300 hover:bg-gold-500 hover:text-navy-950 sm:block"
          >
            Agendar consulta
          </button>

          {/* Botão do menu — celular e tablet */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileOpen}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gold-500/40 text-gold-400 lg:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </div>
      <div className="h-px w-full gold-rule opacity-30" />

      {/* Menu do celular */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden lg:hidden"
          >
            <div className="max-h-[calc(100vh-72px)] overflow-y-auto overscroll-contain px-5 pb-8 pt-4 sm:px-8" data-lenis-prevent>
              <p className="font-mono-meta text-[10px] uppercase tracking-[0.3em] text-gold-400">Áreas de atuação</p>
              <ul className="mt-2">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <li key={c.slug}>
                      <Link
                        to={`/areas/${c.slug}`}
                        data-testid={`nav-mobile-area-${c.slug}`}
                        className="flex items-center gap-3 border-b border-navy-700 py-3.5 text-base text-cream"
                      >
                        <Icon className="h-4 w-4 text-gold-400" />
                        {c.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-5 flex flex-col gap-1">
                {[
                  ["Teste: já posso me aposentar?", "#aposentadoria"],
                  ["Localizações", "#localizacoes"],
                  ["Dúvidas frequentes", "#duvidas"],
                  ["Contato", "#contato"],
                ].map(([label, id]) => (
                  <button key={id} onClick={() => go(id)} className="py-2.5 text-left text-base text-mist hover:text-gold-400">
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  openTriage();
                }}
                className="mt-6 w-full rounded-full bg-gold-500 py-4 text-sm font-bold uppercase tracking-widest text-navy-950"
              >
                Agendar consulta
              </button>
              <SocialIcons className="mt-6 justify-center" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <span className="sr-only">{OFFICE.name} — {OFFICE.oab}</span>
    </motion.header>
  );
}
